import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Group, Modal, Stack, Tabs } from '@mantine/core';
import Button from '@ui/components/Button/Button';
import Callout from '@ui/components/Callout/Callout';
import Text from '@ui/components/Text/Text';
import Stepper from '@ui/components/Stepper/Stepper';

import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import { isResponseError } from 'src/new_arch/errors/ResponseError';
import Loader from '../../../../../components/Loader/Loader';
import useAddWorkspaceAddOnMutation from '../../queries/useAddWorkspaceAddOnMutation';
import useRemoveWorkspaceAddOnMutation from '../../queries/useRemoveWorkspaceAddOnMutation';

import './AddOnsModal.scss';
import { PRICING_PLANS_AVAILABLE } from '../../../../../config/global';
import AddOnModalPresenter from './presenter/AddOnsModalPresenter';
import { formatMoney } from '../../utils';

export type AddOnsModalProps = {
	presenter: AddOnModalPresenter
};

type AddOnsModalTab = 'add' | 'remove';

const MODAL_STYLES = {
	header: {
		backgroundColor: 'var(--color-bg-container)',
		padding: '15px 22px',
		borderBottom: '1px solid var(--color-outline-light)',
		margin: 0
	},
	title: {
		fontFamily: 'Open Sans, sans-serif',
		color: 'var(--mantine-color-space-9)',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '22px',
		letterSpacing: '0px',
		textAlign: 'center'
	},
	close: {
		color: 'var(--color-outline-strong)'
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'stretch',
		padding: 0,
		backgroundColor: 'var(--color-bg-container)'
	},
	content: {
		backgroundColor: 'var(--color-bg-container)'
	}
} as const;

export const AddOnsModal = ( {
	presenter
}: AddOnsModalProps ) => {
	const opened = useDelayedOpening();
	useEffect( presenter.onMount, [] );

	const [ activeTab, setActiveTab ] = useState<AddOnsModalTab>( 'add' );
	const [ qty, setQty ] = useState<number>( 1 );
	const [ errorMessage, setErrorMessage ] = useState<string | null>( null );

	const isSeats = presenter.isSeatsSelected;
	const isAdding = activeTab === 'add';
	const { inPlanTrial } = presenter;
	const isAnnual = presenter.workspace?.hasYearlySubscriptionInterval ?? false;
	const workspaceID = presenter.workspace?.id;
	const remainingSeats = presenter.workspace?.remainingSeats ?? 0;
	const currentAddOnQuantity = presenter.addOnQuantity ?? 0;

	useEffect( () => {
		setQty( 1 );
		setErrorMessage( null );
	}, [ activeTab, currentAddOnQuantity, isAdding, isSeats ] );

	const onMutationError = ( error: unknown ) => {
		const message = isResponseError( error )
			? error.message
			: 'Something went wrong. Please try again.';
		setErrorMessage( message );
	};
	const onMutationSuccess = () => {
		setErrorMessage( null );
		presenter.close();
	};

	const addMutation = useAddWorkspaceAddOnMutation( {
		workspaceID: workspaceID as number,
		onSuccess: onMutationSuccess,
		onError: onMutationError
	} );
	const removeMutation = useRemoveWorkspaceAddOnMutation( {
		workspaceID: workspaceID as number,
		onSuccess: onMutationSuccess,
		onError: onMutationError
	} );

	const isMutating = addMutation.isLoading || removeMutation.isLoading;

	const monthlyUnitPrice = useMemo( () => {
		if ( !presenter.addOn ) return 0;
		return isAnnual
			? ( presenter.addOn.annualPriceCost ?? 0 ) / 12
			: ( presenter.addOn.monthlyPriceCost ?? 0 );
	}, [ presenter.addOn, isAnnual ] );

	const total = qty * monthlyUnitPrice;
	const annualTotal = isAnnual ? qty * ( presenter.addOn?.annualPriceCost ?? 0 ) : 0;

	const seatLabel = qty === 1 ? 'seat' : 'seats';
	const storageLabel = `${qty} pack${qty === 1 ? '' : 's'}`;
	const unitLabel = isSeats ? seatLabel : storageLabel;

	const modalTitle = isSeats ? 'Manage Seats' : 'Manage Storage';

	const packSizeBytes = ( presenter.addOnIncludedAmount as number | undefined ) ?? 0;
	const storageUsedBytes = presenter.workspace?.storageUsedInBytes ?? 0;
	const includedStorageBytes = presenter.workspace?.includedStorage ?? 0;
	const storageAfterRemoveOne = includedStorageBytes + Math.max( 0, currentAddOnQuantity - 1 ) * packSizeBytes;
	const allStorageInUse = !isAdding
		&& !isSeats
		&& currentAddOnQuantity > 0
		&& storageUsedBytes > storageAfterRemoveOne;
	const allInUse = isSeats
		? ( !isAdding && remainingSeats === 0 && currentAddOnQuantity > 0 )
		: allStorageInUse;

	const buildConfirmLabel = () => {
		if ( !isSeats ) return isAdding ? 'Add Storage' : 'Remove Storage';
		if ( !isAdding ) return `Remove ${qty} ${unitLabel}`;
		return `Add ${qty} ${unitLabel}`;
	};
	const confirmLabel = buildConfirmLabel();

	const packSizeLabel = ( presenter.addOnIncludedAmount?.formatBytes?.() ) || '';
	const buildLabelTitle = () => {
		if ( isSeats ) return isAdding ? 'Additional Seats' : 'Total Seats';
		return `${isAdding ? '+' : '-'}${packSizeLabel} Storage`;
	};
	const labelTitle = buildLabelTitle();

	const monthlyUnitPriceRaw = presenter.addOn?.monthlyPriceCost ?? 0;
	const annualUnitPrice = presenter.addOn?.annualPriceCost ?? 0;
	const buildSubtitle = () => {
		if ( isSeats ) {
			return isAdding
				? `$${formatMoney( monthlyUnitPrice )} each billed monthly.`
				: 'Only unused seats can be removed';
		}
		const showAnnual = isAdding && isAnnual;
		const period = showAnnual ? 'year' : 'month';
		const price = showAnnual ? annualUnitPrice : monthlyUnitPriceRaw;
		const sign = isAdding ? '' : '-';
		return `$${sign}${formatMoney( price )}/ per ${period}`;
	};

	const onConfirm = () => {
		if ( !presenter.addOnPrice ) return;
		const params = { addOnPriceID: presenter.addOnPrice.id, quantity: qty };
		if ( isAdding ) addMutation.mutate( params );
		else removeMutation.mutate( params );
	};

	const onManageMembers = () => {
		if ( workspaceID ) presenter.history?.replace?.( `/user/workspaces/${workspaceID}/members` );
		presenter.close();
	};

	const onManageAssets = () => {
		if ( workspaceID ) presenter.history?.replace?.( `/user/workspaces/${workspaceID}` );
		presenter.close();
	};

	const removableQuantity = useMemo( () => {
		if ( isSeats ) {
			return Math.min( currentAddOnQuantity, remainingSeats );
		}
		if ( packSizeBytes <= 0 ) return currentAddOnQuantity;
		const usedAddonStorageBytes = Math.max( 0, storageUsedBytes - includedStorageBytes );
		const packsInUse = Math.ceil( usedAddonStorageBytes / packSizeBytes );
		return Math.max( 0, currentAddOnQuantity - packsInUse );
	}, [
		currentAddOnQuantity,
		includedStorageBytes,
		isSeats,
		packSizeBytes,
		remainingSeats,
		storageUsedBytes
	] );
	const stepperMax = isAdding
		? Infinity
		: Math.max( 1, removableQuantity );
	const stepperValue = Math.min( qty, stepperMax );

	if ( !PRICING_PLANS_AVAILABLE ) return null;

	return (
		<Modal
			opened={opened}
			onClose={presenter.close}
			title={modalTitle}
			styles={MODAL_STYLES}
			size="420px"
		>
			<div className="AddOnsModal">
				{!inPlanTrial && (
					<Tabs
						value={activeTab}
						onTabChange={value => value && setActiveTab( value as AddOnsModalTab )}
						styles={{
							tabsList: {
								borderBottom: '1px solid var(--color-outline-light)',
								padding: '0 16px'
							},
							tab: {
								'padding': '8px 0',
								'borderBottom': '2px solid transparent',
								'&[data-active]': {
									borderBottomColor: 'var(--color-primary-base)'
								},
								'&[data-active] .mantine-Tabs-tabLabel': {
									fontFamily: 'Open Sans, sans-serif',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									letterSpacing: '0.14px',
									color: 'var(--mantine-color-space-9)'
								}
							},
							tabLabel: {
								fontFamily: 'Open Sans, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '22px',
								letterSpacing: '0.14px',
								color: 'var(--color-text-light)'
							}
						}}
					>
						<Tabs.List grow>
							<Tabs.Tab value="add">Add</Tabs.Tab>
							<Tabs.Tab value="remove">Remove</Tabs.Tab>
						</Tabs.List>
					</Tabs>
				)}
				<Loader active={presenter.loading} />
				{!presenter.loading && (
					<Stack spacing={30} sx={{ padding: '20px', width: '100%', boxSizing: 'border-box' }}>
						{inPlanTrial && isSeats && (
							<Callout
								type="promotional"
								variant="outlined"
								text="Seats will keep access during your trial and become paid when you upgrade."
							/>
						)}

						{allInUse ? (
							<>
								<Callout
									type="error"
									withIcon={false}
									px={12}
									styles={{
										root: {
											background: 'var(--color-feedback-on-error)',
											border: '1px solid var(--mantine-color-red-3)'
										}
									}}
									text={(
										<Stack spacing={4} w="100%">
											<Text
												style={{
													fontFamily: 'Open Sans, sans-serif',
													fontWeight: 600,
													fontSize: '14px',
													lineHeight: '22px',
													letterSpacing: '0.14px',
													color: 'var(--color-feedback-error)'
												}}
											>
												{isSeats ? 'All seats are currently in use' : 'This storage units are currently in use'}
											</Text>
											<Text
												style={{
													fontFamily: 'Open Sans, sans-serif',
													fontWeight: 400,
													fontSize: '12px',
													lineHeight: '18px',
													letterSpacing: '0.13px',
													color: 'var(--color-text-primary)'
												}}
											>
												{isSeats
													? 'To remove a seat, first remove a member from the workspace.'
													: 'To remove storage, first remove assets from workspace.'}
											</Text>
										</Stack>
									)}
								/>
								<Group position="right" spacing={21} w="100%">
									<Button
										variant="subtle"
										onClick={presenter.close}
										styles={{
											root: {
												width: 85,
												height: 36,
												padding: 0,
												borderRadius: 4,
												color: 'var(--color-text-secondary)',
												fontSize: 14
											}
										}}
									>
										Cancel
									</Button>
									<Button
										onClick={isSeats ? onManageMembers : onManageAssets}
										styles={{
											root: {
												minWidth: 143,
												width: 'auto',
												height: 36,
												paddingLeft: 16,
												paddingRight: 16,
												borderRadius: 4,
												fontSize: 14,
												whiteSpace: 'nowrap'
											}
										}}
									>
										{isSeats ? 'Manage Members' : 'Manage Assets'}
									</Button>
								</Group>
							</>
						) : (
							<>
								{!inPlanTrial && (
									<Group position="apart" align="center" w="100%">
										<Stack spacing={2}>
											<Text variant="h4">
												{labelTitle}
											</Text>
											<Text variant="footnote" color="var(--color-text-light)">
												{buildSubtitle()}
											</Text>
										</Stack>
										<Stepper
											value={stepperValue}
											min={1}
											max={stepperMax}
											onChange={setQty}
											aria-label={isSeats ? 'Seat quantity' : 'Storage quantity'}
										/>
									</Group>
								)}

								{!inPlanTrial && qty > 0 && (
									<Callout
										type={isAdding ? 'info' : 'error'}
										withIcon={false}
										px={12}
										styles={{
											root: {
												background: isAdding ? 'var(--color-primary-subtle)' : 'var(--color-feedback-on-error)',
												border: `1px solid ${isAdding ? 'var(--mantine-color-blue-3)' : 'var(--mantine-color-red-3)'}`
											}
										}}
										text={(
											<Stack spacing={4} w="100%">
												<Group position="apart" align="flex-start" w="100%" noWrap>
													<Stack spacing={4} style={{ flex: 1, minWidth: 0 }}>
														<Text
															style={{
																fontFamily: 'Open Sans, sans-serif',
																fontWeight: 600,
																fontSize: '14px',
																lineHeight: '22px',
																letterSpacing: '0.14px',
																color: isAdding ? 'var(--color-primary-base)' : 'var(--color-feedback-error)'
															}}
														>
															{( () => {
																if ( isAdding ) return isSeats ? 'Additional amount charge' : 'New Total';
																return isSeats ? 'Saves from next cycle' : 'Monthly Savings';
															} )()}
														</Text>
														<Text
															style={{
																fontFamily: 'Open Sans, sans-serif',
																fontWeight: 400,
																fontSize: '12px',
																lineHeight: '18px',
																letterSpacing: '0.13px',
																color: 'var(--color-text-primary)'
															}}
														>
															{( () => {
																if ( isAdding ) return 'Prorated for this billing cycle.';
																return isSeats
																	? 'Removal takes effect immediately. Unused time until next billing date is not refunded.'
																	: 'No refunds for removed storage, unused time is forfeited';
															} )()}
														</Text>
														{isAnnual && isAdding && (
															<Text
																style={{
																	fontFamily: 'Open Sans, sans-serif',
																	fontWeight: 400,
																	fontSize: '12px',
																	lineHeight: '18px',
																	letterSpacing: '0.13px',
																	color: 'var(--color-text-light)'
																}}
															>
																{`$${formatMoney( annualTotal )} billed annually`}
															</Text>
														)}
													</Stack>
													<Group spacing={2} align="flex-start" noWrap>
														<Text
															style={{
																fontFamily: 'Open Sans, sans-serif',
																fontWeight: 700,
																fontSize: '20px',
																lineHeight: '28px',
																letterSpacing: '-0.38px',
																textAlign: 'right',
																color: isAdding ? 'var(--color-primary-base)' : 'var(--color-feedback-error)'
															}}
														>
															{isAdding ? `+$${formatMoney( total )}` : `-$${formatMoney( total )}`}
														</Text>
														<Text
															style={{
																fontFamily: 'Open Sans, sans-serif',
																fontWeight: 400,
																fontSize: '12px',
																lineHeight: '18px',
																letterSpacing: '0.13px',
																textAlign: 'right',
																color: 'var(--color-text-light)'
															}}
														>
															/mo
														</Text>
													</Group>
												</Group>
											</Stack>
										)}
									/>
								)}

								{errorMessage && (
									<Callout type="error" text={errorMessage} />
								)}

								{presenter.additionalInformation && (
									<Callout type="promotional" text={presenter.additionalInformation} />
								)}

								<Group position="right" spacing={21} w="100%">
									<Button
										variant="outline"
										onClick={presenter.close}
										disabled={isMutating}
										styles={{
											root: {
												width: 85,
												height: 36,
												padding: 0,
												borderRadius: 4,
												border: '1px solid var(--color-outline-strong)',
												color: 'var(--color-text-secondary)',
												fontSize: 14
											}
										}}
									>
										Cancel
									</Button>
									<Button
										onClick={onConfirm}
										disabled={
											isMutating
											|| qty === 0
											|| ( !isAdding && currentAddOnQuantity === 0 )
										}
										loading={isMutating}
										color={isAdding ? undefined : 'red'}
										loaderProps={{ color: 'white' }}
										styles={{
											root: {
												'width': 143,
												'height': 36,
												'borderRadius': 4,
												'fontSize': 14,
												'paddingLeft': 0,
												'paddingRight': 0,
												'&[data-disabled]:not([data-loading])': {
													backgroundColor: 'var(--color-bg-container)',
													color: 'var(--color-text-light)',
													border: '1px solid var(--color-outline-default)',
													opacity: 1
												},
												'&[data-loading]': {
													color: 'var(--color-primary-on)',
													opacity: 1
												}
											},
											inner: { padding: 0 },
											label: {
												padding: 0,
												color: 'inherit'
											}
										}}
									>
										{confirmLabel}
									</Button>
								</Group>
							</>
						)}
					</Stack>
				)}
			</div>
		</Modal>
	);
};

export default observer( AddOnsModal as React.FC<AddOnsModalProps> );
